import { useTranslation } from "@sagot/i18n";
import { cn } from "@sagot/ui";
import type { ReactNode } from "react";
import { passwordScore } from "../../lib/password";
import "../auth-register.css";

const SEGMENT_TONE = ["bg-danger-fill", "bg-accent", "bg-primary", "bg-success"] as const;
const LABEL_TONE = ["text-fg-subtle", "text-danger", "text-warning", "text-primary-ink", "text-success"] as const;
const SEGMENTS = [0, 1, 2, 3] as const;

/** `[data-pw-strength]`: 4 segments + label (screens §0 algorithm). */
export function PasswordStrength({ value, id }: { value: string; id?: string }) {
  const { t } = useTranslation();
  const score = passwordScore(value);
  const label = value ? t(`auth.password.strength${String(score)}`) : t("auth.password.hintEmpty");
  return (
    <div id={id} className="pw-strength mt-2" data-score={value ? score : 0}>
      <div className="grid grid-cols-4 gap-1.5" aria-hidden="true">
        {SEGMENTS.map((i) => (
          <span key={i} data-on={i < score || undefined} className={cn("pw-seg h-1.5 rounded-pill transition-colors", i < score ? SEGMENT_TONE[score - 1] : "bg-line-strong")} />
        ))}
      </div>
      <div className={cn("pw-label mt-1.5 text-xs", value ? cn("font-semibold", LABEL_TONE[score]) : "font-medium text-fg-subtle")} aria-live="polite">
        {value ? <span className="sr-only">{t("auth.password.strengthLabel", { label })}</span> : null}
        <span aria-hidden={value ? true : undefined}>{label}</span>
      </div>
    </div>
  );
}

interface PasswordMeterProps {
  /** 0 = empty, 1 to 4 = the mockup's `pwScore`. */
  score: 0 | 1 | 2 | 3 | 4;
  /** The line under the bar (what is missing, or the strength word). */
  help: ReactNode;
  /** The help line turns red (the mockup's `.a-help.err`). */
  invalid?: boolean;
  /** id of the help line, for the password field's `aria-describedby`. */
  helpId: string;
}

/** Register wizard meter (mockup `.strength` + `#pwHelp`): four bars that fill red, amber, green-yellow, green, and one help line. */
export function PasswordMeter({ score, help, invalid, helpId }: PasswordMeterProps) {
  return (
    <>
      <div className="strength" data-s={score} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <p id={helpId} className={cn("hint", invalid && "hint-err")} aria-live="polite">
        {help}
      </p>
    </>
  );
}
